import type { AnchorHTMLAttributes } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { cx } from '@/lib/cx';
import { mailtoHref } from '@/lib/mail';

/**
 * A neutral text link. Accent (navy) is reserved for primary actions, the active
 * nav row and focus, so a link reads in the ordinary text colour and announces
 * itself with an underline on hover and on keyboard focus. The focus ring is the
 * global `:focus-visible` rule in `index.css`, which already matches `a`.
 *
 * ⚠️ `TextLink` is a router `<Link>`, never `<Button href>` — that renders a plain
 * `<a>`, so pressing it reloads the whole bundle and discards every cached read.
 */
type Tone = 'default' | 'muted';

const TONE: Record<Tone, string> = {
  default: 'text-fg',
  muted: 'text-fg-muted hover:text-fg',
};

const BASE =
  'rounded-sm underline-offset-2 decoration-hairline-strong transition-colors duration-150 ease-out hover:underline focus-visible:underline motion-reduce:transition-none';

export function TextLink({ tone = 'default', className, ...props }: LinkProps & { tone?: Tone }) {
  return <Link {...props} className={cx(BASE, TONE[tone], className)} />;
}

/**
 * The same look for a link that leaves the app. `target` and `rel` are exactly
 * what the caller passes: a `mailto:`/`tel:` must not open a tab, and an
 * `https:` link that should is the caller's decision, not this component's.
 */
export function ExternalTextLink({
  tone = 'default',
  className,
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement> & { href: string; tone?: Tone }) {
  return <a {...props} className={cx(BASE, TONE[tone], className)} />;
}

/**
 * An email address as a `mailto:` link (owner, 2026-10-16: every address in
 * the app). The label defaults to the address itself. Clicks stop here so a
 * link inside a clickable row or card does not also open the row.
 */
export function EmailLink({
  address,
  tone = 'default',
  className,
  children,
  onClick,
  ...props
}: Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & { address: string; tone?: Tone }) {
  return (
    <a
      {...props}
      href={mailtoHref(address)}
      onClick={(event) => {
        event.stopPropagation();
        onClick?.(event);
      }}
      className={cx(BASE, TONE[tone], '[overflow-wrap:anywhere]', className)}
    >
      {children ?? address}
    </a>
  );
}
